import NiceModal from "@ebay/nice-modal-react";
import { ContractPerson, ContractPersonRole } from "@repo/api";
import { FC, useCallback, useMemo } from "react";
import { capitalizeFirstLetter, formatDate } from "../../helpers";
import { generateAccordionsSkeletonRows } from "../../helpers/SkeletonGenerator";
import { useTypedTranslation } from "../../hooks";
import { Accordions } from "../Accordions";
import { Modals } from "../Modals";
import { PersonAccordionTitle } from "./PersonAccordionTitle";
import { PersonDetail } from "./PersonDetail";
import { PersonDetailContactInfo } from "./PersonDetailContactInfo";
import { PersonDetailCorrespondenceAddress } from "./PersonDetailCorrespondenceAddress";
import { PersonDetailPermanentResidence } from "./PersonDetailPermanentResidence";
import { PersonDetailPersonalData } from "./PersonDetailPersonalData";
import * as Styled from "./PersonsAccordion.styled";

type PersonsAccordionProps = {
    persons: ContractPerson[];
    isLoading?: boolean;
    helpBox?: React.ReactNode;
};

export const PersonsAccordion: FC<PersonsAccordionProps> = ({ persons, isLoading, helpBox }) => {
    const { t } = useTypedTranslation();

    const handleChangePersonalDataClick = useCallback(() => {
        NiceModal.show(Modals.ModalConfirm, {
            title: t("life.global.changePersonalData"),
            content: () => <>{t("life.global.changePersonalDataMessage")}</>,
        });
    }, [t]);

    const handleChangePermanentResidenceClick = useCallback(() => {
        NiceModal.show(Modals.ModalConfirm, {
            title: t("life.global.changePermanentResidence"),
            content: () => <>{t("life.global.changePermanentResidenceMessage")}</>,
        });
    }, [t]);

    const rows = useMemo<
        {
            id: string;
            TitleContent: React.JSX.Element;
            Bodycontent: React.JSX.Element;
        }[]
    >(() => {
        return (
            persons.map((person) => {
                const isOwner = person.role !== ContractPersonRole.Insured;
                const role = (() => {
                    if (person.role === ContractPersonRole.OwnerAndInsured) return t("life.global.ownerAndInsuredPerson");
                    if (person.role === ContractPersonRole.Owner) return t("life.global.owner");
                    return capitalizeFirstLetter(t("life.global.insuredPerson"));
                })();
                const name =
                    `${person.person.titlePrefix ?? ""} ${person.person.firstName ?? ""} ${person.person.lastName ?? ""} ${person.person.titleSuffix ?? ""}`.trim();
                const birthDateFormatted = formatDate(person.person.birthDate, "-");
                const isCorrespondenceAddressVisible = isOwner === true;

                return {
                    id: person.person.id.toString(),
                    TitleContent: <PersonAccordionTitle name={name} birthDate={birthDateFormatted} role={role} />,
                    Bodycontent: (
                        <Styled.PersonsAccordionBodyBox>
                            <Styled.PersonsAccordionBodyInnerBox
                                $gridRowMd={isCorrespondenceAddressVisible ? "1" : "span 2"}
                            >
                                <PersonDetail
                                    title={t("global.personalData")}
                                    onChangeClick={handleChangePersonalDataClick}
                                >
                                    <PersonDetailPersonalData person={person} birthDateFormatted={birthDateFormatted} />
                                </PersonDetail>
                            </Styled.PersonsAccordionBodyInnerBox>

                            <Styled.PersonsAccordionBodyInnerBox $gridColumnMd="2">
                                <PersonDetail title={t("global.contactInfo")}>
                                    <PersonDetailContactInfo person={person} isOwner={isOwner} />
                                </PersonDetail>
                            </Styled.PersonsAccordionBodyInnerBox>

                            {isCorrespondenceAddressVisible && (
                                <Styled.PersonsAccordionBodyInnerBox $gridColumnMd="1">
                                    <PersonDetail title={t("global.correspondenceAddress")}>
                                        <PersonDetailCorrespondenceAddress person={person} />
                                    </PersonDetail>
                                </Styled.PersonsAccordionBodyInnerBox>
                            )}

                            <Styled.PersonsAccordionBodyInnerBox $gridColumnMd="2">
                                <PersonDetail
                                    title={t("global.permanentResidence")}
                                    onChangeClick={handleChangePermanentResidenceClick}
                                >
                                    <PersonDetailPermanentResidence person={person} />
                                </PersonDetail>
                            </Styled.PersonsAccordionBodyInnerBox>
                        </Styled.PersonsAccordionBodyBox>
                    ),
                };
            })
        );
    }, [persons, t, handleChangePersonalDataClick, handleChangePermanentResidenceClick]);

    return (
        <>
            {isLoading ? <Accordions rows={generateAccordionsSkeletonRows(1)} /> : <Accordions rows={rows ?? []} />}
            {helpBox}
        </>
    );
};
